import React from 'react';

import { Link } from '@mui/material';
import {
  HelpTypes,
  IAdminBroadcastUserGroupApiData,
} from '@pharmaplan/common';

import { Constants, telephoneInputMask } from '../../../../helpers/Constants';
import strings from '../../../../localization';

import styles from './styles';

const { text, dropdown, radio } = Constants.formInputType;

export enum LeadCreationMethod {
  email = 'email',
  broadcastGroup = 'broadcastGroup',
}

interface IMenuItem {
  key: string;
  label: string;
}

interface ILeadFieldConfigOptions {
  broadcastGroupOptions: Array<IMenuItem>;
  creationMethod: LeadCreationMethod;
  isEdit: boolean;
}

export const getEligibleBroadcastGroupMembers = (
  members: Array<IAdminBroadcastUserGroupApiData>,
  userType: HelpTypes,
) => {
  const mappedEmails = new Set<string>();

  return members.filter((member) => {
    const normalizedEmail = member.email?.trim().toLowerCase();
    const matchesUserType = `${member.userType}` === `${userType}`;

    if (!normalizedEmail || !matchesUserType || mappedEmails.has(normalizedEmail)) {
      return false;
    }

    mappedEmails.add(normalizedEmail);
    return true;
  });
};

export const leadFieldConfig = (
  campaignOptions: Array<IMenuItem>,
  userType: HelpTypes,
  {
    broadcastGroupOptions,
    creationMethod,
    isEdit,
  }: ILeadFieldConfigOptions,
) => {
  const userTypeDropdown = [
    { key: HelpTypes.pharmacy, label: strings.pharmacy },
    { key: HelpTypes.pharmacist, label: strings.pharmacist },
    { key: HelpTypes.technicians, label: strings.technicians },
    { key: HelpTypes.students, label: strings.student },
  ];

  const isPharmacy = userType === HelpTypes.pharmacy;

  const creationMethodField = {
    xs: 12,
    key: 'creationMethod',
    name: 'creationMethod',
    label: strings.addLeadsUsing,
    style: {
      marginTop: 0,
      marginBottom: 10,
    },
    type: radio,
    horizontal: true,
    radioItems: [
      { value: LeadCreationMethod.email, label: strings.email },
      {
        value: LeadCreationMethod.broadcastGroup,
        label: strings.broadcastGroup,
      },
    ],
  };

  const campaignField = {
    xs: 3,
    key: '3',
    name: 'campaignId',
    label: strings.campaignName,
    type: dropdown,
    menuItems: campaignOptions,
    requireLabel: true,
    customTextFieldContainer: styles.fieldContainer,
  };

  const userTypeField = {
    xs: 3,
    key: 'userType',
    name: 'userType',
    label: strings.userType,
    type: dropdown,
    menuItems: userTypeDropdown,
    requireLabel: true,
    disabled: true,
    customTextFieldContainer: styles.fieldContainer,
  };

  const notesField = {
    xs: 6,
    key: '7',
    name: 'notes',
    label: strings.notes,
    type: text,
    placeholder: ' ',
    multiline: true,
    customTextFieldContainer: styles.fieldContainer,
  };

  if (!isEdit && creationMethod === LeadCreationMethod.broadcastGroup) {
    return [
      creationMethodField,
      campaignField,
      userTypeField,
      {
        xs: 3,
        key: 'broadcastGroupId',
        name: 'broadcastGroupId',
        label: strings.broadcastGroup,
        type: dropdown,
        menuItems: broadcastGroupOptions,
        requireLabel: true,
        customTextFieldContainer: styles.fieldContainer,
      },
      notesField,
    ];
  }

  return [
    !isEdit && creationMethodField,
    {
      xs: 3,
      key: '1',
      name: 'name',
      label: strings.name,
      type: text,
      requireLabel: true,
      placeholder: ' ',
      customTextFieldContainer: styles.fieldContainer,
    },
    {
      xs: 3,
      key: '2',
      name: 'email',
      label: strings.email,
      type: text,
      requireLabel: true,
      placeholder: ' ',
      customTextFieldContainer: styles.fieldContainer,
    },
    {
      label: strings.phone,
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
      name: 'phone',
      maxLength: 12,
    },
    isPharmacy && {
      xs: 3,
      key: '8',
      label: strings.postalCode,
      type: text,
      customTextFieldContainer: styles.fieldContainer,
      requireLabel: true,
      placeholder: ' ',
      name: 'postalCode',
    },
    campaignField,
    userTypeField,
    notesField,
  ].filter(Boolean) as Array<{
    key: string;
    name: string;
    label: string;
    type: string;
    requireLabel?: boolean;
    placeholder?: string;
    customTextFieldContainer?: Record<string, any>;
    multiline?: boolean;
    mask?: Array<string | RegExp>;
    maxLength?: number;
    menuItems?: Array<{ key: string; label: string }>;
    disabled?: boolean;
    horizontal?: boolean;
    radioItems?: Array<{ value: string; label: string }>;
  }>;
};

const currentScreen = (
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  <Link
    key="curr"
    onClick={() => {
      window.history?.back();
    }}
    href="#"
    sx={[styles.noUnderline, styles.prevLink]}
  >
    {strings.leads}
  </Link>
);

export const createCrumbs = [
  currentScreen,
  <Link
    href={Constants.paths.admin.createLead}
    sx={styles.noUnderline}
    key="create"
  >
    {strings.createLead}
  </Link>,
];

export const editCrumbs = [
  currentScreen,
  <Link
    href={Constants.paths.admin.editLead}
    sx={styles.noUnderline}
    key="edit"
  >
    {strings.editLead}
  </Link>,
];
